import React, { useEffect } from 'react';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import BannerHero from '../components/home/BannerHero';
import Features from '../components/home/Features';
import HowItWorksImage from '../components/home/HowItWorksImage';
import Audience from '../components/home/Audience';
import Pricing from '../components/home/Pricing';
import Testimonials from '../components/home/Testimonials';
import LatestPosts from '../components/home/LatestPosts';
import FAQNew, { FAQS } from '../components/home/FAQNew';
import CTA from '../components/home/CTA';
import { siteConfig } from '../config/site';

const HomePage: React.FC = () => {
  // Inject JSON-LD schemas for SEO: Organization + WebSite + FAQPage + Product.
  useEffect(() => {
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      logo: `${siteConfig.domain}/logo512.png`,
      description: siteConfig.brand.description,
      email: siteConfig.contact.orderEmail,
      contactPoint: [{
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: siteConfig.contact.phoneDisplay,
        availableLanguage: ['English'],
      }],
    };
    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteConfig.domain}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const product = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${siteConfig.brand.name} — Flash USDT Generator`,
      description: siteConfig.brand.description,
      brand: { '@type': 'Brand', name: siteConfig.brand.name },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '15',
        highPrice: '5000',
        offerCount: '6',
        url: `${siteConfig.domain}/pricing`,
      },
    };
    const tags: HTMLScriptElement[] = [];
    for (const [id, obj] of [
      ['ld-organization', org],
      ['ld-website', website],
      ['ld-faq', faq],
      ['ld-product', product],
    ] as const) {
      let s = document.getElementById(id) as HTMLScriptElement | null;
      if (!s) {
        s = document.createElement('script');
        s.id = id; s.type = 'application/ld+json';
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(obj);
      tags.push(s);
    }
    return () => tags.forEach((t) => t.remove());
  }, []);

  return (
    <Layout>
      <SEO
        title={`${siteConfig.brand.name} — Professional Flash USDT Generator for TRC-20, ERC-20 & BEP-20`}
        description={`Buy ${siteConfig.brand.name} — the professional flash USDT generator. Multi-chain TRC-20 / ERC-20 / BEP-20, 50+ wallets, 24/7 WhatsApp support, 365-day flash validity, up to $10M daily cap.`}
        keywords="flash USDT generator, flash USDT generator pro, buy flash USDT generator, professional USDT flasher, TRC-20 flash USDT, ERC-20 flash USDT, BEP-20 flash USDT, USDT simulation software, tether flasher, flash USDT license"
      />
      <BannerHero />
      <Features />
      <HowItWorksImage />
      <Audience />
      <Pricing />
      <Testimonials />
      <LatestPosts />
      <FAQNew />
      <CTA />
    </Layout>
  );
};

export default HomePage;
